"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { AuthManager, AuthManagerLegacy } from "@v2/auth/client";
import { InvalidCredentialsError } from "@v2/auth/client/errors/invalid-credentials";
import { LegacyProfileFetchError } from "@v2/auth/client/errors/legacy-profile-fetch";
import {
  AUTH_MANAGER_CHANGE_EVENT,
  AUTH_MANAGER_LEGACY_CHANGE_EVENT,
} from "@v2/auth/client/config";
import type { AuthUser, LegacyUserProfile } from "@/lib/(v2)/auth/types";
import { useAuthStore } from "@v2/ui/stores/auth-store";

/**
 * PROFILE CONTEXT (v1) — tradutor v2 → v1.
 *
 * Não orquestra autenticação. A fonte de verdade é o `AuthManager` (core) +
 * `AuthManagerLegacy` (profile enriquecido); este provider só:
 *   1. inicializa o store v2 ao montar e mantém o espelho em sincronia com
 *      o `AuthManager` via `AUTH_MANAGER_CHANGE_EVENT`;
 *   2. carrega o profile legacy (`role`, `equipe_id`, `is_aprovador`, …) via
 *      `AuthManagerLegacy.load(token)` sempre que o core ficar autenticado
 *      sem um profile cacheado pro mesmo `userId`;
 *   3. combina os dois shapes no `UserData` que componentes v1 consomem;
 *   4. intercepta `window.fetch` pra injetar `Authorization: Bearer` em
 *      chamadas v1 a `/api` e Server Actions, com refresh+retry em 401.
 *
 * Quando todos os componentes v1 forem migrados pra v2, este arquivo, o
 * `AuthManagerLegacy` e a rota `/api/profile/me` podem ser deletados.
 */

interface EquipeData {
  id: string;
  nome: string;
  codigo: string;
  is_suporte: boolean;
}

interface UserData {
  id: string;
  name: string;
  email: string;
  role: string;
  external_username?: string | null;
  contato?: string | null;
  company?: string | null;
  department?: string | null;
  active?: boolean;
  user_metadata?: Record<string, unknown>;
  equipe_id?: string | null;
  equipe?: EquipeData | null;
  is_aprovador?: boolean;
  avatar_url?: string | null;
}

interface ProfileContextType {
  isAuthenticated: boolean;
  user: UserData | null;
  profile: UserData | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  loading: boolean;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

const PUBLIC_AUTH_ROUTES = ["/login", "/cadastro", "/logout"] as const;
const AUTH_ENDPOINTS_TO_IGNORE_401 = [
  "/api/auth/login",
  "/api/auth/logout",
  "/api/auth/refresh",
] as const;

function mergeUserData(core: AuthUser, legacy: LegacyUserProfile): UserData {
  return {
    id: core.id,
    name: core.name,
    email: core.email,
    role: legacy.role ?? "",
    external_username: legacy.external_username,
    contato: legacy.contato,
    company: legacy.company,
    department: legacy.department,
    active: legacy.active,
    equipe_id: legacy.equipe_id,
    is_aprovador: legacy.is_aprovador,
    avatar_url: legacy.avatar_url,
  };
}

function isOnPublicRoute(): boolean {
  if (typeof window === "undefined") return false;
  return PUBLIC_AUTH_ROUTES.some((r) =>
    window.location.pathname.startsWith(r),
  );
}

// ── Interceptor global de window.fetch (singleton) ───────────────────────────
// Injeta `Authorization: Bearer` em requests /api + Server Actions e, em 401,
// tenta refresh+retry; se o refresh falha, desloga. Instalado UMA única vez e
// nunca restaurado — sobrevive à navegação soft v1↔v2 sem corrida pelo
// `window.fetch` global. Lê tudo de singletons (`AuthManager`) + o
// estado de logout em escopo de módulo (`logoutState`), por isso não precisa
// reinstalar por mount nem depender do ciclo de vida do provider.
const logoutState = { active: false };
let fetchInterceptorInstalled = false;

function installFetchInterceptor(): void {
  if (typeof window === "undefined" || fetchInterceptorInstalled) return;
  fetchInterceptorInstalled = true;
  const originalFetch = window.fetch;

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    const currentOrigin = window.location.origin;
    const isApiRequest =
      url.startsWith("/api") || url.startsWith(`${currentOrigin}/api`);
    const isServerAction = new Headers(init?.headers).has("Next-Action");
    const shouldInjectAuth = isApiRequest || isServerAction;

    const auth = AuthManager.getInstance();
    let nextInit: RequestInit = init ?? {};

    if (shouldInjectAuth) {
      const token = auth.getAccessToken();
      if (token) {
        const headers = new Headers(nextInit.headers);
        if (!headers.has("Authorization")) {
          headers.set("Authorization", `Bearer ${token}`);
        }
        nextInit = { ...nextInit, headers };
      }
    }

    const response = await originalFetch(input, nextInit);
    if (!shouldInjectAuth || response.status !== 401) return response;

    const normalizedUrl = url.startsWith(currentOrigin)
      ? url.replace(currentOrigin, "")
      : url;
    const skip401 = AUTH_ENDPOINTS_TO_IGNORE_401.some((e) =>
      normalizedUrl.startsWith(e),
    );
    if (
      skip401 ||
      isOnPublicRoute() ||
      logoutState.active ||
      !auth.hasSession()
    ) {
      return response;
    }

    const refreshed = await auth.refreshAccessToken();
    if (!refreshed) {
      logoutState.active = true;
      AuthManagerLegacy.getInstance().clear();
      await auth.logout();
      if (!isOnPublicRoute()) window.location.replace("/login");
      return response;
    }

    const retryHeaders = new Headers(nextInit.headers);
    const newToken = auth.getAccessToken();
    if (newToken) retryHeaders.set("Authorization", `Bearer ${newToken}`);
    return originalFetch(input, { ...nextInit, headers: retryHeaders });
  };
}

export function ProfileProvider({ children }: { children: ReactNode }) {
  const coreUser = useAuthStore((s) => s.user);
  const coreIsAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const coreLoading = useAuthStore((s) => s.loading);
  const storeInitialize = useAuthStore((s) => s.initialize);
  const storeSyncFromManager = useAuthStore((s) => s.syncFromManager);
  const storeLogin = useAuthStore((s) => s.login);
  const storeLogout = useAuthStore((s) => s.logout);

  const [legacyProfile, setLegacyProfile] = useState<LegacyUserProfile | null>(
    () => AuthManagerLegacy.getInstance().getProfile(),
  );

  // (1) Inicializa o store v2 + escuta o AuthManager pra manter o store em
  // sincronia (refresh em background, login/logout em outra aba, etc.).
  useEffect(() => {
    storeInitialize();
    const onAuthChange = () => storeSyncFromManager();
    window.addEventListener(AUTH_MANAGER_CHANGE_EVENT, onAuthChange);
    return () => {
      window.removeEventListener(AUTH_MANAGER_CHANGE_EVENT, onAuthChange);
    };
  }, [storeInitialize, storeSyncFromManager]);

  // (2) Espelha mudanças do AuthManagerLegacy no state local.
  useEffect(() => {
    const onLegacyChange = () => {
      setLegacyProfile(AuthManagerLegacy.getInstance().getProfile());
    };
    window.addEventListener(AUTH_MANAGER_LEGACY_CHANGE_EVENT, onLegacyChange);
    return () => {
      window.removeEventListener(
        AUTH_MANAGER_LEGACY_CHANGE_EVENT,
        onLegacyChange,
      );
    };
  }, []);

  // (3) Hidrata/limpa o profile legacy conforme o core autentica/desautentica.
  useEffect(() => {
    const authLegacy = AuthManagerLegacy.getInstance();

    if (!coreIsAuthenticated || !coreUser) {
      if (authLegacy.getProfile()) authLegacy.clear();
      return;
    }

    const cached = authLegacy.getProfile();
    if (cached && cached.id === coreUser.id) {
      // Garante que o state local reflete o cache (no caso de o init ter
      // visto null e o cache ter sido populado por outro caminho v2).
      setLegacyProfile(cached);
      return;
    }
    if (cached && cached.id !== coreUser.id) authLegacy.clear();

    const token = AuthManager.getInstance().getAccessToken();
    if (!token) return;

    let cancelled = false;
    authLegacy
      .load(token)
      .then((profile) => {
        if (cancelled) return;
        setLegacyProfile(profile);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[PROFILE] legacy profile load failed:", err);
        if (err instanceof LegacyProfileFetchError) {
          // Sem profile enriquecido, componentes v1 quebram — derruba a sessão
          // pra forçar novo login.
          void storeLogout();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [coreIsAuthenticated, coreUser, storeLogout]);

  // (4) Interceptor de window.fetch — instalado UMA vez, global e idempotente,
  // pra sobreviver à navegação soft v1↔v2 sem corrida pelo `window.fetch`
  // global. A lógica vive no `installFetchInterceptor` (escopo de módulo);
  // aqui só garantimos a instalação ao montar o provider.
  useEffect(() => {
    installFetchInterceptor();
  }, []);

  // (5) REFRESH PROATIVO em background (#159) — renova o access token a ~75% do
  // TTL, ANTES de expirar, pra a aba ociosa nunca chegar ao 401 que deslogava.
  // Caminho feliz: reagenda a partir do exp do token novo. Em FALHA (transitória
  // — a definitiva já limpa a sessão e desmonta este efeito), usa BACKOFF
  // EXPONENCIAL + jitter, com teto de tentativas, pra NÃO martelar /refresh a
  // cada 1s. Também renova ao voltar foco/visibilidade (timers de aba em
  // background são estrangulados pelo browser).
  useEffect(() => {
    if (!coreIsAuthenticated) return;
    if (typeof window === "undefined") return;
    const auth = AuthManager.getInstance();
    const BACKOFF_BASE_MS = 1_000;
    const BACKOFF_MAX_MS = 60_000;
    const MAX_FALHAS = 6;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const agendar = (falhas = 0) => {
      if (cancelled) return;
      // Desiste de martelar após falhas seguidas; o foco/visibilidade reativa.
      if (falhas >= MAX_FALHAS) return;
      let delay: number;
      if (falhas === 0) {
        const proativo = auth.msUntilRefresh(0.75);
        if (proativo == null) return;
        delay = Math.max(proativo, 1_000);
      } else {
        // 1s → 2s → 4s … até 60s, + jitter pra dessincronizar abas/clientes.
        const base = Math.min(BACKOFF_BASE_MS * 2 ** (falhas - 1), BACKOFF_MAX_MS);
        delay = base + Math.floor(Math.random() * 500);
      }
      timer = setTimeout(async () => {
        const ok = await auth.refreshAccessToken();
        agendar(ok ? 0 : falhas + 1);
      }, delay);
    };
    agendar(0);

    // Ao reativar a aba, se já passou do ponto de refresh, renova e reinicia o
    // ciclo proativo (zera o backoff).
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const delay = auth.msUntilRefresh(0.75);
      if (delay != null && delay <= 0) {
        void auth.refreshAccessToken().then(() => {
          if (timer) clearTimeout(timer);
          agendar(0);
        });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [coreIsAuthenticated]);

  // (5) REFRESH PROATIVO em background (#159) — renova o access token a ~75% do
  // TTL, ANTES de expirar, pra a aba ociosa nunca chegar ao 401 que deslogava.
  // Caminho feliz: reagenda a partir do exp do token novo. Em FALHA (transitória
  // — a definitiva já limpa a sessão e desmonta este efeito), usa BACKOFF
  // EXPONENCIAL + jitter, com teto de tentativas, pra NÃO martelar /refresh a
  // cada 1s. Também renova ao voltar foco/visibilidade (timers de aba em
  // background são estrangulados pelo browser).
  useEffect(() => {
    if (!coreIsAuthenticated) return;
    if (typeof window === "undefined") return;
    const auth = AuthManager.getInstance();
    const BACKOFF_BASE_MS = 1_000;
    const BACKOFF_MAX_MS = 60_000;
    const MAX_FALHAS = 6;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const agendar = (falhas = 0) => {
      if (cancelled) return;
      // Desiste de martelar após falhas seguidas; o foco/visibilidade reativa.
      if (falhas >= MAX_FALHAS) return;
      let delay: number;
      if (falhas === 0) {
        const proativo = auth.msUntilRefresh(0.75);
        if (proativo == null) return;
        delay = Math.max(proativo, 1_000);
      } else {
        // 1s → 2s → 4s … até 60s, + jitter pra dessincronizar abas/clientes.
        const base = Math.min(BACKOFF_BASE_MS * 2 ** (falhas - 1), BACKOFF_MAX_MS);
        delay = base + Math.floor(Math.random() * 500);
      }
      timer = setTimeout(async () => {
        const ok = await auth.refreshAccessToken();
        agendar(ok ? 0 : falhas + 1);
      }, delay);
    };
    agendar(0);

    // Ao reativar a aba, se já passou do ponto de refresh, renova e reinicia o
    // ciclo proativo (zera o backoff).
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const delay = auth.msUntilRefresh(0.75);
      if (delay != null && delay <= 0) {
        void auth.refreshAccessToken().then(() => {
          if (timer) clearTimeout(timer);
          agendar(0);
        });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [coreIsAuthenticated]);

  const login = useCallback(
    async (email: string, password: string): Promise<boolean> => {
      try {
        await storeLogin(email, password);
        return true;
      } catch (err) {
        if (err instanceof InvalidCredentialsError) return false;
        console.error("[PROFILE] login error:", err);
        return false;
      }
    },
    [storeLogin],
  );

  const logout = useCallback(async (): Promise<void> => {
    if (logoutState.active) return;
    logoutState.active = true;
    try {
      AuthManagerLegacy.getInstance().clear();
      await storeLogout();
      if (typeof window !== "undefined" && !isOnPublicRoute()) {
        window.location.replace("/login");
      }
    } finally {
      logoutState.active = false;
    }
  }, [storeLogout]);

  const hasMatchingLegacy =
    coreUser !== null &&
    legacyProfile !== null &&
    legacyProfile.id === coreUser.id;

  const user = hasMatchingLegacy
    ? mergeUserData(coreUser!, legacyProfile!)
    : null;

  // Loading derivado — fica true enquanto o core inicializa OU enquanto há
  // sessão autenticada sem o profile legacy correspondente carregado. Isso
  // garante que `user` e `loading` mudam atomicamente no mesmo render
  // (sem corrida entre `setLegacyProfile` e um `setLegacyLoading(false)`).
  const needsLegacy = coreIsAuthenticated && coreUser !== null;
  const loading = coreLoading || (needsLegacy && !hasMatchingLegacy);

  const value: ProfileContextType = {
    isAuthenticated: coreIsAuthenticated && user !== null,
    user,
    profile: user,
    login,
    logout,
    loading,
  };

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
}

export function useAuth(): ProfileContextType {
  const context = useContext(ProfileContext);

  if (!context) {
    console.error(
      "[PROFILE] useAuth deve ser usado dentro de <ProfileProvider>",
    );
    return {
      isAuthenticated: false,
      user: null,
      profile: null,
      login: async () => false,
      logout: async () => {},
      loading: true,
    };
  }

  return context;
}
